import React, {useState} from 'react';
import {diApi} from '@di-shared/api/di';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import BrakeSevereComparisonGraph, {BrakeSevereComparisonGraphType} from '@di-shared/features/diag/brake/BrakeSevereComparisonGraph';
import BrakeSevereComparisonTooltip from '@di-shared/features/diag/brake/BrakeSevereComparisonTooltip';
import useBrakePadSeverity, {BrakeSevereLevel} from '@di-shared/features/diag/brake/useBrakePadSeverity';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface BrakeSevereComparisonProps {
  vin: string;
}

const IMAGE_FALLBACK_HIGHT = 135;

function BrakeSevereComparison({vin}: BrakeSevereComparisonProps) {
  const {getSevereLevel} = useBrakePadSeverity({vin});

  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.brakePad.getBrake1), vin],
    queryFn: () => diApi.brakePad.getBrake1({vin}),
    select: ({data}) => data,
  });

  if (!data) {
    return <BrakeSevereComparisonNoData />;
  }
  const {calculatedDate, severeTripCount, avgSevereCount} = data;
  const updatedDate = parseDateWithFormat(calculatedDate);
  /**
   * @link https://jira.hmg-corp.io/browse/DRIVINGINS-623
   * 정용훈 Scott Jeong 책임매니저 드라이빙인사이트팀 added a comment - Yesterday
   *
   * 검증계를 다시 아래와 같이 변경 개발하고자 합니다.
   *
   * 평균횟수 1 미만 시 소수점 셋째자리에서 올림하여 둘째자리까지 표출 (ex : 0.004 → 0.01)
   * 평균횟수 1 이상 시 소수점 둘째자리에서 올림하여 첫째자리까지 표출 (ex : 1.001 → 1.1)
   * 대략적인 개발 일정 산정 가능하실까요?
   */
  const roundedSevereTripCount = severeTripCount < 1 ? Math.ceil(severeTripCount * 100) / 100 : Math.ceil(severeTripCount * 10) / 10;
  const roundedAverageSevereTripCount = avgSevereCount < 1 ? Math.ceil(avgSevereCount * 100) / 100 : Math.ceil(avgSevereCount * 10) / 10;
  const severeLevel = getSevereLevel(roundedSevereTripCount, roundedAverageSevereTripCount);

  switch (severeLevel) {
    case BrakeSevereLevel.NORMAL:
      return (
        <BrakeSevereComparisonNormal
          updatedDate={updatedDate}
          severeTripCount={roundedSevereTripCount}
          averageSevereTripCount={roundedAverageSevereTripCount}
          severeGraphRatio={roundedSevereTripCount / roundedAverageSevereTripCount}
          averageGraphRatio={1}
        />
      );
    case BrakeSevereLevel.CAUTION:
      return (
        <BrakeSevereComparisonCaution
          updatedDate={updatedDate}
          severeTripCount={roundedSevereTripCount}
          averageSevereTripCount={roundedAverageSevereTripCount}
          severeGraphRatio={1}
          averageGraphRatio={1}
        />
      );
    case BrakeSevereLevel.SEVERE:
    default:
      return (
        <BrakeSevereComparisonSevere
          updatedDate={updatedDate}
          severeTripCount={roundedSevereTripCount}
          averageSevereTripCount={roundedAverageSevereTripCount}
          severeGraphRatio={1}
          averageGraphRatio={roundedAverageSevereTripCount / roundedSevereTripCount}
        />
      );
  }
}

export default BrakeSevereComparison;

interface BrakeSevereComparisonCaseProps {
  severeTripCount: number;
  averageSevereTripCount: number;
  severeGraphRatio: number;
  averageGraphRatio: number;
  updatedDate: Date | null;
}

function BrakeSevereComparisonNormal({
  severeTripCount,
  averageSevereTripCount,
  updatedDate,
  severeGraphRatio,
  averageGraphRatio,
}: BrakeSevereComparisonCaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const [randomNumber] = useState<number>(Math.round(Math.random()));
  const randomMessage = [t('T30059'), t('T30078')][randomNumber];

  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30055')}
        </h2>
        <div
          className="section_heading_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30056')}
          <BrakeSevereComparisonTooltip />
        </div>
        <span className="section_heading_caption">
          {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_visual visual_brake_pad">
        <ImageSelector
          style={{height: IMAGE_FALLBACK_HIGHT}}
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_good_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_good.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/hyundai/brake_pad_good_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/hyundai/brake_pad_good.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/genesis/brake_pad_good_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/genesis/brake_pad_good.png')}
        />
      </div>
      <BrakeSevereComparisonGraph
        severeGraphType={BrakeSevereComparisonGraphType.GOOD}
        severeTripCount={severeTripCount}
        averageSevereTripCount={averageSevereTripCount}
        severeGraphRatio={severeGraphRatio}
        averageGraphRatio={averageGraphRatio}
      />
      <p className="brake_pad_guide">{randomMessage}</p>
    </>
  );
}

function BrakeSevereComparisonCaution({
  severeTripCount,
  averageSevereTripCount,
  updatedDate,
  severeGraphRatio,
  averageGraphRatio,
}: BrakeSevereComparisonCaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();

  const [randomNumber] = useState<number>(Math.round(Math.random()));
  const randomMessage = [t('T30059'), t('T30078')][randomNumber];

  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30058')}
        </h2>
        <div
          className="section_heading_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30056')}
          <BrakeSevereComparisonTooltip />
        </div>
        <span className="section_heading_caption">
          {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_visual visual_brake_pad">
        <ImageSelector
          style={{height: IMAGE_FALLBACK_HIGHT}}
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_caution.png')}
        />
      </div>
      <BrakeSevereComparisonGraph
        severeGraphType={BrakeSevereComparisonGraphType.CAUTION}
        severeTripCount={severeTripCount}
        averageSevereTripCount={averageSevereTripCount}
        severeGraphRatio={severeGraphRatio}
        averageGraphRatio={averageGraphRatio}
      />
      <p className="brake_pad_guide">{randomMessage}</p>
    </>
  );
}

function BrakeSevereComparisonSevere({
  severeTripCount,
  averageSevereTripCount,
  updatedDate,
  severeGraphRatio,
  averageGraphRatio,
}: BrakeSevereComparisonCaseProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [randomNumber] = useState<number>(Math.round(Math.random()));
  const randomMessage = [t('T30059'), t('T30078')][randomNumber];
  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30060')}
        </h2>
        <div
          className="section_heading_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30056')}
          <BrakeSevereComparisonTooltip />
        </div>
        <span className="section_heading_caption">
          {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_visual visual_brake_pad">
        <ImageSelector
          style={{height: IMAGE_FALLBACK_HIGHT}}
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_warning.png')}
        />
      </div>
      <BrakeSevereComparisonGraph
        severeGraphType={BrakeSevereComparisonGraphType.WARNING}
        severeTripCount={severeTripCount}
        averageSevereTripCount={averageSevereTripCount}
        severeGraphRatio={severeGraphRatio}
        averageGraphRatio={averageGraphRatio}
      />
      <p className="brake_pad_guide">{randomMessage}</p>
    </>
  );
}

export function BrakeSevereComparisonNoData() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30061')}
        </h2>
        <div
          className="section_heading_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30056')}
          <BrakeSevereComparisonTooltip />
        </div>
      </div>
      <div className="section_visual visual_brake_pad">
        <ImageSelector
          style={{height: IMAGE_FALLBACK_HIGHT}}
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
        />
      </div>
      <BrakeSevereComparisonGraph
        severeGraphType={BrakeSevereComparisonGraphType.NODATA}
        severeTripCount={0}
        averageSevereTripCount={0}
        severeGraphRatio={0}
        averageGraphRatio={0}
      />
      <p className="brake_pad_guide" />
    </>
  );
}

export function BrakeSevereComparisonError() {
  const {t} = useInferableTranslation();

  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30062')}
        </h2>
        <div
          className="section_heading_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30056')}
          <BrakeSevereComparisonTooltip />
        </div>
      </div>
      <div className="section_visual visual_brake_pad">
        <ImageSelector
          style={{height: IMAGE_FALLBACK_HIGHT}}
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/visual/brake_pad_nodata.png')}
        />
      </div>
      <BrakeSevereComparisonGraph
        severeGraphType={BrakeSevereComparisonGraphType.NODATA}
        severeTripCount={'-'}
        averageSevereTripCount={'-'}
        severeGraphRatio={0}
        averageGraphRatio={0}
      />
      <p className="brake_pad_guide" />
    </>
  );
}

export function BrakeSevereComparisonLoading() {
  return (
    <>
      <div className="section_heading type_main">
        <h2
          className="section_heading_title skeleton"
          style={{height: 60}}
        />
        <div
          className="section_heading_desc skeleton"
          style={{height: 36}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{height: 15}}
        />
      </div>
      <div className="section_visual visual_brake_pad">
        <div
          className="section_visual_img skeleton"
          style={{height: 134.64}}
        />
      </div>
      <div
        className="brakepad_graph_area skeleton"
        style={{height: 72}}
      />
      <p
        className="brake_pad_guide skeleton"
        style={{height: 40}}
      />
    </>
  );
}
